import type { DriverContext } from "@skaile/workspaces/plugin-registry";
import { type PluginRegistry, pluginRegistry } from "@skaile/workspaces/plugin-registry";
import type { ModelEntry } from "./models.js";
import type { AgentConfig, AgentDriver, DriverInfo } from "./types.js";

// Build-time backend gating. `bun build --compile --define:__INCLUDE_*__=...`
// folds these to literal booleans so an excluded backend's driver module and
// its SDK are dead-code-stripped from the binary. Under tsup / vitest / bun
// run-on-source no `--define` runs and the `typeof` guard defaults to `true` —
// every backend is available (the SDKs stay optional peers).
declare const __INCLUDE_CLAUDE_SDK__: boolean;
declare const __INCLUDE_CODEX__: boolean;
const INCLUDE_CLAUDE_SDK =
  typeof __INCLUDE_CLAUDE_SDK__ === "undefined" ? true : __INCLUDE_CLAUDE_SDK__;
const INCLUDE_CODEX = typeof __INCLUDE_CODEX__ === "undefined" ? true : __INCLUDE_CODEX__;

/**
 * Static capability metadata for every built-in backend.
 *
 * Pure data — this object imports no driver module and references no backend
 * SDK. {@link listDrivers} returns it (merged with any third-party drivers)
 * so selection UIs can enumerate every backend without loading a single
 * driver module. The matching driver module's `DriverTarget` is registered
 * into the plugin registry by {@link registerBuiltinDrivers}.
 *
 * @docLink packages/bridge/api-reference#registry
 */
export const BUILTIN_DRIVER_CATALOG: Record<string, DriverInfo> = {
  omp: {
    id: "omp",
    name: "omp (oh-my-pi)",
    modelAgnostic: true,
    supportsInBandAbort: true,
  },
  "claude-sdk": {
    id: "claude-sdk",
    name: "Claude Agent SDK",
    modelAgnostic: false,
    supportsInBandAbort: true,
  },
  codex: {
    id: "codex",
    name: "OpenAI Codex",
    modelAgnostic: false,
    supportsInBandAbort: true,
  },
  echo: {
    id: "echo",
    name: "Echo (E2E mock)",
    modelAgnostic: true,
    supportsInBandAbort: false,
  },
  gemini: {
    id: "gemini",
    name: "Gemini CLI",
    modelAgnostic: false,
    supportsInBandAbort: false,
  },
  qwen: {
    id: "qwen",
    name: "Qwen Code",
    modelAgnostic: true,
    supportsInBandAbort: false,
  },
  opencode: {
    id: "opencode",
    name: "OpenCode",
    modelAgnostic: true,
    supportsInBandAbort: false,
  },
  goose: {
    id: "goose",
    name: "Goose",
    modelAgnostic: true,
    supportsInBandAbort: false,
  },
  continue: {
    id: "continue",
    name: "Continue CLI",
    modelAgnostic: true,
    supportsInBandAbort: false,
  },
};

/**
 * Back-compat alias for {@link BUILTIN_DRIVER_CATALOG}. Consumers and tests that
 * predate the plugin-registry refactor still import `DRIVER_CATALOG`.
 *
 * @docLink packages/bridge/api-reference#registry
 */
export const DRIVER_CATALOG = BUILTIN_DRIVER_CATALOG;

let _driversRegistered = false;

/**
 * Register every built-in driver's `DriverTarget` into the plugin registry.
 *
 * This is the single id → module map in bridge core. Every specifier is a
 * literal string so `bun --compile` can statically bundle the driver module
 * (and, transitively, its SDK). The in-process-SDK backends (`claude-sdk`,
 * `codex`) are wrapped in build-constant guards so an excluded backend's
 * `await import` folds to dead code in the compiled binary. `omp` and `echo`
 * carry no SDK weight and are always available.
 *
 * **Static import discipline.** Every `import()` specifier below must be a
 * string literal — never aliased through a variable. Indirection defeats
 * `bun --compile`'s static analysis and silently drops the module from the
 * binary.
 *
 * Idempotent: guarded by a module flag so a second call is a no-op (a duplicate
 * `register` would otherwise throw `RegistrationError`).
 *
 * @docLink packages/bridge/api-reference#registry
 */
export async function registerBuiltinDrivers(
  registry: PluginRegistry = pluginRegistry,
): Promise<void> {
  if (registry === pluginRegistry && _driversRegistered) return;

  if (!registry.get("driver", "omp")) {
    const m = await import("@skaile/workspaces/drivers/omp");
    registry.register("driver", m.ompDriverTarget);
  }
  if (!registry.get("driver", "echo")) {
    const m = await import("@skaile/workspaces/drivers/echo");
    registry.register("driver", m.echoDriverTarget);
  }
  if (INCLUDE_CLAUDE_SDK && !registry.get("driver", "claude-sdk")) {
    const m = await import("@skaile/workspaces/drivers/claude-sdk");
    registry.register("driver", m.claudeSdkDriverTarget);
  }
  if (INCLUDE_CODEX && !registry.get("driver", "codex")) {
    const m = await import("@skaile/workspaces/drivers/codex");
    registry.register("driver", m.codexDriverTarget);
  }
  // Subprocess CLI backends spawn an external binary the user installs
  // separately — no bundled SDK to dead-code-strip, so (like omp/echo) they
  // carry no build constant and are always registered.
  if (!registry.get("driver", "gemini")) {
    const m = await import("@skaile/workspaces/drivers/gemini");
    registry.register("driver", m.geminiDriverTarget);
  }
  if (!registry.get("driver", "qwen")) {
    const m = await import("@skaile/workspaces/drivers/qwen");
    registry.register("driver", m.qwenDriverTarget);
  }
  if (!registry.get("driver", "opencode")) {
    const m = await import("@skaile/workspaces/drivers/opencode");
    registry.register("driver", m.opencodeDriverTarget);
  }
  if (!registry.get("driver", "goose")) {
    const m = await import("@skaile/workspaces/drivers/goose");
    registry.register("driver", m.gooseDriverTarget);
  }
  if (!registry.get("driver", "continue")) {
    const m = await import("@skaile/workspaces/drivers/continue");
    registry.register("driver", m.continueDriverTarget);
  }

  if (registry === pluginRegistry) _driversRegistered = true;
}

/**
 * Creates a new driver instance for the given backend id.
 *
 * Ensures the built-in driver targets are registered (idempotent), then looks
 * up the target in the plugin registry and calls its `create()`. The returned
 * driver has not been started yet — call `driver.start()` before sending
 * prompts.
 *
 * @param id - Registered driver identifier (e.g. `"omp"`, `"claude-sdk"`).
 * @param config - Configuration forwarded to the driver constructor.
 * @param ctx - Optional driver context (logger / abort signal). Defaults to `{}`.
 * @returns A freshly constructed (not yet started) {@link AgentDriver}.
 * @throws {Error} When `id` is a known backend excluded from this build, or
 *   when `id` does not match any registered driver at all.
 * @docLink packages/bridge/api-reference#registry
 */
export async function createDriver(
  id: string,
  config: AgentConfig,
  ctx?: DriverContext,
): Promise<AgentDriver> {
  const target = await resolveDriverTarget(id);
  return target.create(config, ctx ?? {});
}

/**
 * Resolve a registered driver target by id, ensuring the built-ins are
 * registered first. Throws the canonical "not bundled" / "unknown driver"
 * errors so every caller (`createDriver`, `listModelsForDriver`) reports an
 * unrecognized id the same way instead of silently degrading.
 *
 * @throws {Error} When `id` is a known backend excluded from this build, or
 *   when `id` does not match any registered driver at all.
 */
async function resolveDriverTarget(id: string) {
  await registerBuiltinDrivers();
  const target = pluginRegistry.get("driver", id);
  if (!target) {
    if (id in BUILTIN_DRIVER_CATALOG) {
      throw new Error(`Agent driver "${id}" is not bundled in this build.`);
    }
    throw new Error(
      `Unknown agent driver: "${id}". Available: ${listDrivers()
        .map((d) => d.id)
        .join(", ")}`,
    );
  }
  return target;
}

/**
 * Returns the capability metadata for every available driver.
 *
 * Enumerates every built-in backend from {@link BUILTIN_DRIVER_CATALOG} without
 * loading a single driver module or backend SDK, then merges in any third-party
 * driver targets registered into the plugin registry that are not already in
 * the catalog. Use this to populate driver selection UIs.
 *
 * @returns Array of {@link DriverInfo} objects, one per available driver.
 * @docLink packages/bridge/api-reference#registry
 */
export function listDrivers(): DriverInfo[] {
  const merged = new Map<string, DriverInfo>();
  for (const info of Object.values(BUILTIN_DRIVER_CATALOG)) {
    merged.set(info.id, info);
  }
  for (const meta of pluginRegistry.list("driver")) {
    if (!merged.has(meta.id)) {
      // Third-party drivers carry no DriverInfo row; read the capability flags
      // off the registered target (defaulting to conservative false) so a
      // model-agnostic / in-band-abort backend can still advertise them.
      const target = pluginRegistry.get("driver", meta.id);
      merged.set(meta.id, {
        id: meta.id,
        name: meta.displayName,
        modelAgnostic: target?.modelAgnostic ?? false,
        supportsInBandAbort: target?.supportsInBandAbort ?? false,
      });
    }
  }
  return [...merged.values()];
}

/**
 * List models available to a specific driver without starting a full agent session.
 *
 * Ensures the built-in driver targets are registered (idempotent), then
 * delegates to the target's optional `listModels()`. API keys are forwarded so
 * drivers can authenticate against provider REST APIs if needed. Throws on an
 * unknown / not-bundled id (same contract as {@link createDriver}); returns `[]`
 * only when the target exists but exposes no `listModels`.
 *
 * @param driverId - Registered driver id (e.g. `"omp"`, `"claude-sdk"`, `"codex"`).
 * @param apiKeys - Provider API keys keyed by provider name (e.g. `{ anthropic: "sk-..." }`).
 *   Pass `settings.apiKeys ?? {}` from forge-assistant's resolved settings.
 * @throws {Error} When `driverId` is unknown or excluded from this build.
 * @docLink packages/bridge/api-reference#registry
 */
export async function listModelsForDriver(
  driverId: string,
  apiKeys: Record<string, string>,
): Promise<ModelEntry[]> {
  const target = await resolveDriverTarget(driverId);
  return (await target.listModels?.(apiKeys)) ?? [];
}
